import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, CanDeactivateFn, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { firstValueFrom, Subscription } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { StoreAdministrationService } from '../../../services/store/store-administration.service';
import { StoreAdministrationDto } from '../../../services/store/store-administration.types';

type EditableField = 'place' | 'address' | 'district' | 'signature';
type StoreForm = Record<EditableField, string>;

@Component({
  selector: 'app-store-detail', standalone: true, templateUrl: './store-detail.component.html', styleUrls: ['./store-detail.component.css'],
  imports: [CommonModule, RouterLink, FormsModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatIconModule, MatProgressSpinnerModule],
})
export class StoreDetailComponent implements OnInit, OnDestroy {
  store?: StoreAdministrationDto;
  form: StoreForm = { place: '', address: '', district: '', signature: '' };
  original: StoreForm = { ...this.form };
  resets = { place: false, address: false, district: false };
  private beforeReset: Partial<StoreForm> = {};
  private routeSubscription?: Subscription;
  private requestGeneration = 0;
  private requestedId?: number;
  loading = true; saving = false; availabilitySaving = false; error = ''; saveError = ''; availabilityError = ''; saveSuccess = ''; availabilitySuccess = '';
  constructor(private route: ActivatedRoute, private router: Router, private administration: StoreAdministrationService, private cdr: ChangeDetectorRef) {}
  get dirty(): boolean { return !!this.store && (this.resets.place || this.resets.address || this.resets.district || (Object.keys(this.form) as EditableField[]).some(key => this.form[key] !== this.original[key])); }
  get invalid(): boolean { return !this.form.place.trim() || !this.form.district.trim() || !this.form.signature.trim(); }
  get originLabel(): string {
    switch (this.store?.origin?.toLowerCase()) {
      case 'manual': return 'Manuelt oprettet';
      case 'imported': return 'Importeret';
      case 'unknown': return 'Ukendt';
      default: return this.store?.origin ?? 'Ukendt kilde';
    }
  }
  get availabilityLabel(): string {
    if (this.store?.overrides.availability == null) return 'Følger upstream';
    return this.store.overrides.availability ? 'Lokalt aktiveret' : 'Lokalt deaktiveret';
  }
  @HostListener('window:beforeunload', ['$event'])
  protectUnload(event: BeforeUnloadEvent): void {
    if (this.dirty) { event.preventDefault(); event.returnValue = ''; }
  }
  ngOnInit(): void {
    this.routeSubscription?.unsubscribe();
    this.routeSubscription = this.route.paramMap.subscribe(params => this.loadStore(Number(params.get('id'))));
  }
  ngOnDestroy(): void {
    this.requestGeneration++;
    this.routeSubscription?.unsubscribe();
  }
  private loadStore(id: number): void {
    if (this.store && this.store.id !== id && (this.saving || this.availabilitySaving)) {
      void this.router.navigate(['/manage-stores', this.store.id]);
      return;
    }
    // Returning to the previous URL after vetoing reused-route navigation must
    // not reload that store and erase the draft we just chose to keep.
    if (this.store?.id === id && (this.dirty || this.saving || this.availabilitySaving)) {
      if (this.requestedId !== id) {
        this.requestGeneration++;
        this.requestedId = id;
        this.loading = false; this.error = '';
        this.cdr.markForCheck();
      }
      return;
    }
    if (this.store && this.store.id !== id && this.dirty && !window.confirm('Du har ændringer, der ikke er gemt. Vil du forlade siden?')) {
      void this.router.navigate(['/manage-stores', this.store.id]);
      return;
    }
    const generation = ++this.requestGeneration;
    this.requestedId = id;
    this.loading = true; this.error = ''; this.saveError = ''; this.availabilityError = '';
    this.saving = false; this.availabilitySaving = false;
    this.cdr.markForCheck();
    void firstValueFrom(this.administration.get(id)).then(store => {
      if (generation !== this.requestGeneration) return;
      this.store = store; this.setForm(store);
    }).catch(() => {
      if (generation === this.requestGeneration) this.error = 'Butikken kunne ikke hentes.';
    }).finally(() => {
      if (generation === this.requestGeneration) { this.loading = false; this.cdr.markForCheck(); }
    });
  }
  private setForm(store: StoreAdministrationDto): void {
    this.form = { place: store.place ?? '', address: store.address ?? '', district: store.district ?? '', signature: store.signature ?? '' };
    this.original = { ...this.form }; this.resets = { place: false, address: false, district: false };
    this.beforeReset = {};
  }
  stageReset(field: 'place' | 'address' | 'district'): void {
    if (!this.store || this.saving) return;
    const hasValue = field === 'place' ? this.store.overrides.hasPlace : field === 'address' ? this.store.overrides.hasAddress : this.store.overrides.hasDistrict;
    const upstreamValue = this.store.upstream[field];
    if (!hasValue || upstreamValue == null) return;
    if (!this.resets[field]) this.beforeReset[field] = this.form[field];
    this.form[field] = upstreamValue; this.resets[field] = true;
  }
  cancelReset(field: 'place' | 'address' | 'district'): void {
    if (this.saving || !this.resets[field]) return;
    this.form[field] = this.beforeReset[field] ?? this.form[field];
    this.resets[field] = false; delete this.beforeReset[field];
  }
  onFieldChange(field: EditableField): void {
    if (field !== 'signature') { this.resets[field] = false; delete this.beforeReset[field]; }
    this.saveSuccess = ''; this.saveError = '';
  }
  async save(): Promise<void> {
    if (!this.store || this.saving || this.availabilitySaving || !this.dirty || this.invalid) return;
    const body: any = { resetPlaceOverride: this.resets.place, resetAddressOverride: this.resets.address, resetDistrictOverride: this.resets.district };
    for (const field of ['place', 'address', 'district', 'signature'] as EditableField[]) {
      if (field === 'place' && this.resets.place || field === 'address' && this.resets.address || field === 'district' && this.resets.district) continue;
      if (this.form[field] !== this.original[field]) body[field] = this.form[field].trim();
    }
    const generation = this.requestGeneration;
    this.saving = true; this.saveError = ''; this.saveSuccess = '';
    try {
      const updated = await firstValueFrom(this.administration.update(this.store.id, body));
      if (generation !== this.requestGeneration) return;
      this.store = updated; this.setForm(updated); this.saveSuccess = 'Ændringerne er gemt.';
    } catch { if (generation === this.requestGeneration) this.saveError = 'Ændringerne kunne ikke gemmes. Dine oplysninger er bevaret.'; }
    finally { if (generation === this.requestGeneration) { this.saving = false; this.cdr.markForCheck(); } }
  }
  async setAvailability(active: boolean): Promise<void> {
    if (!this.store || this.availabilitySaving || this.saving) return;
    if (!active && !window.confirm(`Vil du deaktivere butik ${this.store.id}, ${this.store.place}? Eksisterende udsendelser fortsætter, og butikkens adgang fortsætter. Butikken kan ikke vælges til nye udsendelser.`)) return;
    const generation = this.requestGeneration;
    this.availabilitySaving = true; this.availabilityError = ''; this.availabilitySuccess = '';
    // Leave the live form untouched, including edits made while this request runs.
    try {
      const updated = await firstValueFrom(this.administration.setAvailability(this.store.id, active));
      if (generation !== this.requestGeneration) return;
      this.store = updated; this.availabilitySuccess = active ? 'Butikken er aktiveret.' : 'Butikken er deaktiveret.';
    } catch { if (generation === this.requestGeneration) this.availabilityError = 'Status kunne ikke ændres. Den gemte status er uændret.'; }
    finally { if (generation === this.requestGeneration) { this.availabilitySaving = false; this.cdr.markForCheck(); } }
  }
  back(): void { void this.router.navigate(['/manage-stores']); }
}

export const storeDetailCanDeactivate: CanDeactivateFn<StoreDetailComponent> = component =>
  (!component.saving && !component.availabilitySaving) && (!component.dirty || window.confirm('Du har ændringer, der ikke er gemt. Vil du forlade siden?'));
